const React = require('react');
const T = require('prop-types');

const { useEffect, useRef, useState } = React;

const internals = {};

module.exports = FallbackImage;
module.exports.internals = internals;

function FallbackImage(props) {

    const {
        baseUrl,
        extensions,
        alt = '',
        defaultSrc,   // shown while probing & if all fail
        ...rest
    } = props;

    const { preloadImage, preloadImageUrl } = internals;

    const [resolvedSrc, setResolvedSrc] = useState(null);
    const abortRef = useRef({ aborted: false });

    useEffect(() => {

        abortRef.current.aborted = false;
        setResolvedSrc(null);

        if (!Array.isArray(extensions) || extensions.length === 0) {
            return;
        }

        const urls = extensions.map((ext) => preloadImageUrl(baseUrl, ext));

        const tryLoad = async () => {

            for (let i = 0; i < urls.length; ++i) {
                const ok = await preloadImage(urls[i], abortRef);
                if (abortRef.current.aborted) {
                    return;
                }

                if (ok) {
                    setResolvedSrc(urls[i]);
                    return;
                }
            }

            // none worked → stick with default
            setResolvedSrc(defaultSrc || null);
        };

        tryLoad();

        return () => {

            abortRef.current.aborted = true;
        };
    }, [baseUrl, JSON.stringify(extensions), defaultSrc]);

    // While probing, show defaultSrc if available
    if (!resolvedSrc) {
        return defaultSrc
            ? <img src={defaultSrc} alt={alt} {...rest} />
            : null;
    }

    return (
        <img
            src={resolvedSrc}
            alt={alt}
            {...rest}
        />
    );
}

internals.preloadImage = async (src, abortRef) => {

    return await new Promise((resolve) => {

        const img = new Image();

        const cleanup = () => {

            img.onload = null;
            img.onerror = null;
        };

        img.onload = () => {

            cleanup();
            resolve(!abortRef.current.aborted);
        };

        img.onerror = () => {

            cleanup();
            resolve(false);
        };

        if (abortRef.current.aborted) {
            return resolve(false);
        }

        img.src = src;
    });
};

internals.preloadImageUrl = (baseUrl, extension) => {

    if (!baseUrl) {
        return extension || '';
    }

    const [path, query = ''] = String(baseUrl).split('?');
    const normalizedQuery = query ? `?${query}` : '';

    return `${path}${extension}${normalizedQuery}`;
};

module.exports.propTypes = {
    baseUrl: T.string.isRequired,
    extensions: T.arrayOf(T.string).isRequired,
    defaultSrc: T.string,
    alt: T.string
};

module.exports.defaultProps = {
    alt: ''
};
